import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { apiErrorDetail } from "@/lib/api-error";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { trackEvent } from "@/lib/engagement";
import { useFieldDebateGenerationStore } from "@/stores/field-debate-generation-store";
import { useLandscapeStore } from "@/stores/useLandscapeStore";
import type { Camp } from "@/hooks/useFieldDebate";

interface GenerateOptions {
  max_papers_per_cluster?: number;
  ws_id?: string;
  paper_ids?: string[];
  apply_scope?: boolean;
}

interface StartResponse {
  job_id: string;
  total_steps: number;
  build_pending?: boolean;
}

interface RefreshStartResponse extends StartResponse {
  no_op?: boolean;
  added?: number;
  removed?: number;
}

interface ActiveJobResponse {
  job_id: string;
  concept_id: string;
  status: string;
  /** "build" | "refresh" — picks the batch_submitted terminal on resume. */
  kind?: string;
  step: number;
  step_label: string;
  total_steps: number;
  pass4_completed: number;
  pass4_total: number;
  error: string | null;
}

const STEP_FALLBACK_LABELS: Record<number, string> = {
  0: "Preparing…",
  1: "Clustering papers",
  2: "Defining camps",
  3: "Assigning papers to camps",
  4: "Mapping claims",
  5: "Analyzing per-camp blind spots",
};

/**
 * Consume the field-debate SSE stream. Pure function — no React state,
 * just dispatches each parsed event to the Zustand store + handles terminal
 * events. Returned promise resolves on terminal event or stream close.
 */
async function consumeFieldDebateStream(
  conceptId: string,
  jobId: string,
  signal: AbortSignal,
  onComplete: () => void,
  onBatchSubmitted: () => void,
  onError: (msg: string) => void,
): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
  const token = await getAuthToken();
  const url = `${baseUrl}/api/concepts/${conceptId}/field-debate/generate/${jobId}/stream`;
  const store = useFieldDebateGenerationStore;

  let receivedTerminal = false;
  try {
    for await (const event of streamSSE(
      url,
      { Authorization: `Bearer ${token}` },
      signal,
    )) {
      const data = event.data ? JSON.parse(event.data) : {};

      // Step lifecycle events: "step:1:start" / "step:2:done" / "step:3:skipped"
      if (event.event.startsWith("step:")) {
        const [, stepRaw, kind] = event.event.split(":");
        const step = Number(stepRaw);
        if (kind === "start") {
          store.getState().setStep(
            step as 1 | 2 | 3 | 4 | 5,
            data.label || STEP_FALLBACK_LABELS[step] || "",
          );
        } else if (kind === "done") {
          if (Array.isArray(data.camps)) {
            store.getState().setPartialCamps(data.camps as Camp[]);
          }
          if (typeof data.completed === "number" && typeof data.total === "number") {
            store.getState().setPass4Progress(data.completed, data.total);
          }
        }
        // step:N:skipped — no state change needed; next step:start fires soon
        continue;
      }

      if (event.event === "preparing") {
        store.getState().setStep(0, data.message || "Preparing…");
      } else if (event.event === "batch_submitted") {
        receivedTerminal = true;
        onBatchSubmitted();
        return;
      } else if (event.event === "complete") {
        receivedTerminal = true;
        onComplete();
        return;
      } else if (event.event === "error") {
        receivedTerminal = true;
        onError(data.error || "Field Debate generation failed");
        return;
      } else if (event.event === "cancelled") {
        receivedTerminal = true;
        store.getState().reset();
        toast.info("Field Debate generation cancelled");
        return;
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") {
      // Caller aborted — store state managed elsewhere.
      return;
    }
    onError(err instanceof Error ? err.message : "SSE stream failed");
    return;
  }

  if (!receivedTerminal) {
    // Stream closed without a terminal event (server restart, network).
    // Fall through to invalidate-and-refetch — the backend may have written
    // the result before the connection dropped.
    onComplete();
  }
}

export function useFieldDebateGenerate(conceptId: string) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const handleComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
    // Quota headroom changed (a build or refresh just landed).
    queryClient.invalidateQueries({ queryKey: ["field-debate-quota", conceptId] });
    useLandscapeStore.getState().clearCache();
    useFieldDebateGenerationStore.getState().finish();
    toast.success("Field Debate ready");
  }, [conceptId, queryClient]);

  const handleBatchSubmitted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
    // A pending batch build consumes quota headroom immediately.
    queryClient.invalidateQueries({ queryKey: ["field-debate-quota", conceptId] });
    useFieldDebateGenerationStore.getState().reset();
    toast.info("Camps ready — assigning papers in the background", {
      description: "The map fills in automatically (usually a few minutes).",
    });
  }, [conceptId, queryClient]);

  // batch_submitted terminal for REFRESH runs only: same invalidations, but
  // arms `pendingRefresh` (the map stays complete/non-partial during a batch
  // refresh, so this flag — not the partial-map poll — drives the
  // "Refreshing…" chip + 20s poll until refresh_meta.refreshed_at advances).
  // Must set the flag AFTER reset(): store.start() at refresh preflight
  // cleared it, and reset() re-spreads initialState for the lifecycle keys.
  const handleRefreshBatchSubmitted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
    queryClient.invalidateQueries({ queryKey: ["field-debate-quota", conceptId] });
    useFieldDebateGenerationStore.getState().reset();
    useFieldDebateGenerationStore
      .getState()
      .setPendingRefresh({ conceptId, since: Date.now() });
    toast.info("Refreshing the map in the background", {
      description:
        "New papers slot into the existing camps (usually a few minutes).",
    });
  }, [conceptId, queryClient]);

  const handleError = useCallback((msg: string) => {
    useFieldDebateGenerationStore.getState().fail(msg);
    toast.error("Field Debate generation failed", { description: msg });
  }, []);

  const start = useCallback(
    async (opts: GenerateOptions = {}) => {
      // Cancel any in-flight SSE stream first.
      abortRef.current?.abort();

      // Optimistic: flip isPending=true synchronously so the rail card,
      // OperationBar pill, and headline status all update immediately on
      // click. Without this the user waits for the active-probe + POST
      // round-trip (1-3s of dead air) before any UI feedback. Real jobId
      // is filled in below once the POST returns. Empty-string jobId is a
      // valid preflight sentinel — `cancel` and SSE attach gate on it.
      const controller = new AbortController();
      abortRef.current = controller;
      useFieldDebateGenerationStore.getState().start(conceptId, "");
      useFieldDebateGenerationStore.getState().setCancelController(controller);

      // Cancel-before-start: the backend is the source of truth. The local
      // store can fall out of sync (auto-fade timer, prior crash, browser
      // restart), so query /generate/active and cancel anything still
      // alive there before issuing the new POST. Without this the second
      // POST 409s and the user has no recourse.
      try {
        const active = await apiClient
          .get(`api/concepts/${conceptId}/field-debate/generate/active`, {
            timeout: 10_000,
          })
          .json<ActiveJobResponse | null>();
        if (
          active &&
          (active.status === "pending" || active.status === "running")
        ) {
          try {
            await apiClient.post(
              `api/concepts/${conceptId}/field-debate/generate/${active.job_id}/cancel`,
              { timeout: 10_000 },
            );
          } catch {
            // Best-effort
          }
        }
      } catch {
        // /generate/active probe failed — proceed; POST will surface the
        // real error if any.
      }
      // User cancelled during preflight → don't re-arm.
      if (controller.signal.aborted) return;

      try {
        trackEvent("feature_use", { feature: "field_debate", action: "started" });

        const res = await apiClient
          .post(`api/concepts/${conceptId}/field-debate/generate`, {
            json: opts,
            timeout: 30_000,
          })
          .json<StartResponse>();

        // User cancelled while POST was in flight → discard the result.
        if (controller.signal.aborted) return;

        if (res.build_pending) {
          // A batch build is already assigning papers for this concept —
          // view-only while pending (spec 2026-07-08).
          useFieldDebateGenerationStore.getState().reset();
          queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
          toast.info("Assignment already in progress for this concept");
          return;
        }

        useFieldDebateGenerationStore.getState().start(conceptId, res.job_id);
        // `store.start` spreads `initialState` which resets cancelController
        // to null. Re-arm it so OperationBar.cancelFieldDebate (which
        // reads ONLY from the store) can synchronously abort the SSE
        // consume during the long Pass 1-4 phase. Without this, a pill
        // cancel during SSE is a silent no-op and a 'complete' event
        // landing before backend's 'cancelled' event flips the store to
        // a "Field Debate ready" toast despite the user's cancel.
        useFieldDebateGenerationStore.getState().setCancelController(controller);

        await consumeFieldDebateStream(
          conceptId,
          res.job_id,
          controller.signal,
          handleComplete,
          handleBatchSubmitted,
          handleError,
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        if ((err as { name?: string })?.name === "AbortError") return;
        // 409 means a job is already running — surface a clearer message
        // and clear our local state so the user can retry after it lands.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          handleError(
            "A Field Debate run is already in progress for this concept — wait for it to finish or cancel it from the operation bar.",
          );
          return;
        }
        // Surface the server's detail (quota copy, guard messages) instead
        // of ky's generic "Request failed with status code N" (2026-07-11
        // quota-403 incident).
        handleError(await apiErrorDetail(err, "Failed to start Field Debate"));
      }
    },
    [conceptId, handleComplete, handleBatchSubmitted, handleError, queryClient],
  );

  // Incremental refresh (FD Stage 3): assign papers that ENTERED scope into
  // the existing (frozen) camps and drop papers that LEFT — never re-derives
  // camps. Mirrors start()'s store lifecycle / abort handling / terminal
  // invalidations exactly; the SSE stream is the same generate contract
  // (step:3/4/5, complete | batch_submitted). Deliberately NO
  // cancel-before-start preflight: refresh must never kill a running build —
  // an active job surfaces as the 409 message below instead.
  const refresh = useCallback(
    async (opts: GenerateOptions = {}) => {
      // Cancel any in-flight SSE stream first (matches start()).
      abortRef.current?.abort();

      // Snapshot any already-pending refresh BEFORE store.start() clears it —
      // if the POST comes back build_pending (that same batch refresh is
      // still assigning), we restore the flag so the chip doesn't vanish.
      const priorPendingRefresh =
        useFieldDebateGenerationStore.getState().pendingRefresh;

      // Optimistic pending state — same rationale as start(): flip
      // isPending=true synchronously so the banner button / rail / pill
      // react on click instead of after the POST round-trip.
      const controller = new AbortController();
      abortRef.current = controller;
      useFieldDebateGenerationStore.getState().start(conceptId, "");
      useFieldDebateGenerationStore.getState().setCancelController(controller);

      try {
        const res = await apiClient
          .post(`api/concepts/${conceptId}/field-debate/refresh`, {
            json: opts,
            timeout: 120_000,
          })
          .json<RefreshStartResponse>();

        // User cancelled while POST was in flight → discard the result.
        if (controller.signal.aborted) return;

        if (res.no_op) {
          // Nothing entered or left scope — no job started, map untouched.
          useFieldDebateGenerationStore.getState().reset();
          queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
          toast.info("Map already reflects the current corpus.");
          return;
        }
        if (res.build_pending) {
          // A batch build is already assigning papers for this concept —
          // attach semantics, view-only while pending (matches start()).
          // No quota invalidation: nothing new was consumed. Restore the
          // pendingRefresh flag store.start() cleared at preflight — if the
          // pending build is a refresh the chip should keep showing (a
          // pending FULL build leaves the map partial, so the chip's
          // non-partial gate suppresses it either way).
          useFieldDebateGenerationStore.getState().reset();
          if (priorPendingRefresh?.conceptId === conceptId) {
            useFieldDebateGenerationStore
              .getState()
              .setPendingRefresh(priorPendingRefresh);
          }
          queryClient.invalidateQueries({ queryKey: ["field-debate", conceptId] });
          toast.info("An update is already in progress for this map.");
          return;
        }

        trackEvent("field_debate_refresh_start", {
          added: res.added ?? 0,
          removed: res.removed ?? 0,
        });

        useFieldDebateGenerationStore.getState().start(conceptId, res.job_id);
        // Re-arm after store.start resets cancelController (see start()).
        useFieldDebateGenerationStore.getState().setCancelController(controller);

        await consumeFieldDebateStream(
          conceptId,
          res.job_id,
          controller.signal,
          handleComplete,
          handleRefreshBatchSubmitted,
          handleError,
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        if ((err as { name?: string })?.name === "AbortError") return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        const msg =
          status === 409
            ? "A Field Debate job is already in progress for this concept — wait for it to finish or cancel it from the operation bar."
            : await apiErrorDetail(err, "Failed to refresh Field Debate");
        handleError(msg);
        // Restore the snapshot AFTER handleError — store.fail() clears
        // pendingRefresh — so a failed refresh POST doesn't hide the chip of
        // a still-in-flight earlier batch refresh (mirrors the build_pending
        // restore above).
        if (priorPendingRefresh?.conceptId === conceptId) {
          useFieldDebateGenerationStore
            .getState()
            .setPendingRefresh(priorPendingRefresh);
        }
      }
    },
    [conceptId, handleComplete, handleRefreshBatchSubmitted, handleError, queryClient],
  );

  const cancel = useCallback(async () => {
    const { jobId, cancelController } = useFieldDebateGenerationStore.getState();
    // Always abort + reset locally, even if there's no real jobId yet
    // (preflight phase). Prefer the store-held controller so this works
    // identically from any entry point (OperationBar pill, tool rail,
    // hook caller); fall back to abortRef if the store hasn't been
    // populated for some reason.
    (cancelController ?? abortRef.current)?.abort();
    useFieldDebateGenerationStore.getState().reset();
    if (!jobId) return;
    try {
      await apiClient.post(
        `api/concepts/${conceptId}/field-debate/generate/${jobId}/cancel`,
      );
    } catch {
      // Best-effort cancel; SSE will deliver the cancelled event.
    }
  }, [conceptId]);

  // Resume an active job on mount / route remount. If a job is already
  // running for this concept (because a previous tab/page kicked it off and
  // navigated away), reattach the SSE stream so progress reappears.
  useEffect(() => {
    const controller = new AbortController();
    let attached = false;

    (async () => {
      try {
        const active = await apiClient
          .get(`api/concepts/${conceptId}/field-debate/generate/active`, {
            timeout: 10_000,
          })
          .json<ActiveJobResponse | null>();

        if (!active || controller.signal.aborted) return;
        // Only reattach if we don't already have a running stream for this job.
        const store = useFieldDebateGenerationStore.getState();
        if (store.jobId === active.job_id && store.isPending) return;

        attached = true;
        useFieldDebateGenerationStore.getState().start(conceptId, active.job_id);
        useFieldDebateGenerationStore.getState().setStep(
          (active.step ?? 0) as 0 | 1 | 2 | 3 | 4 | 5,
          active.step_label || STEP_FALLBACK_LABELS[active.step ?? 0] || "",
        );
        if (active.pass4_total > 0) {
          useFieldDebateGenerationStore.getState().setPass4Progress(
            active.pass4_completed,
            active.pass4_total,
          );
        }
        abortRef.current?.abort();
        abortRef.current = controller;
        await consumeFieldDebateStream(
          conceptId,
          active.job_id,
          controller.signal,
          handleComplete,
          // A reload during the pre-submit window of a batch REFRESH must
          // resume with the refresh terminal (arms pendingRefresh) — the
          // build handler would leave the refreshed map unobserved (Codex
          // review 2026-07-10).
          active.kind === "refresh"
            ? handleRefreshBatchSubmitted
            : handleBatchSubmitted,
          handleError,
        );
      } catch {
        // 404 / network — no active job to resume. Silent fail.
      }
    })();

    return () => {
      // Don't abort ongoing streams on unmount — let them keep running so
      // the Zustand store + OperationBar can show progress on the next
      // route. Only abort if this effect spawned the stream itself AND the
      // component unmounted before the stream attached.
      if (!attached) controller.abort();
    };
  }, [
    conceptId,
    handleComplete,
    handleBatchSubmitted,
    handleRefreshBatchSubmitted,
    handleError,
  ]);

  return { start, refresh, cancel };
}
